/**
 * The module's form panel — the shell every form in the `.erp-app` half of this module opens in,
 * instead of a centred `.form-modal`.
 *
 * DECISIONS.md D-017, the owner twice: *"Let's get rid of pop-up window deesign. Instead of 'Add
 * Business Unit' window, I want you to open the panel on the right side with those fields and
 * follow the UI design for that right panel ... I mean SAve and cance button (Save should be icon
 * Save) etc."* and *"Also, please replace window New Capability Model, by panel on the right side.
 * in general please replace all windows from this app by 'right panel'"*.
 *
 * PRECEDENT: `pages/master-data/ApplicationDetailDrawer.tsx` — the platform's canonical detail
 * panel — by way of `../initiatives/InitiativeDetailPanel.tsx`, which is this module's own clone of
 * it and which already cites each measurement. **Cloned, not imported** (D-003). `FormPanel.css`
 * carries the values and their citations.
 *
 * WHY IT IS A SHELL AND NOT THREE PANELS. This is not a port, so §1.2.1's "do not collapse several
 * source screens into one parameterised component" does not apply — there is no source screen here
 * to be faithful to. What there is is one owner ruling that three forms have to obey identically,
 * and the module already factors its cross-screen chrome this way: `ScreenHeader`, `PageHeader`,
 * `HelpPanel` and `useScreenLock` all live in this folder for the same reason. A second copy of the
 * shell is a second place for the panel to drift.
 *
 * WHAT IT DOES NOT DO, deliberately:
 *   · **No Back and no Maximize.** The precedent's header carries four controls because it is a
 *     two-pane panel you step a whole family through. A form has Close in the header and Cancel in
 *     the footer already; a third way out would be noise.
 *   · **It does not own the form state.** Each screen keeps the state it already had, so converting
 *     a modal to a panel changes the shell and nothing else — which is why no field, label, order,
 *     validation or refusal wording moves in the process.
 *   · **It is not a confirmation.** D-017 keeps "are you sure" as a dialog: no fields, a yes/no
 *     question about an action already chosen, and it must interrupt. `ConfirmDialog`, the
 *     initiative delete modal and the scoring definition's re-score warning stay as they are.
 *
 * THE BODY IS THE ONLY SCROLLER. Header and footer stay put — CLAUDE.md, "A screen header never
 * scrolls". The `<form>` between them passes the height through (`FormPanel.css`'s `.erp-fp-form`).
 *
 * ESCAPE CLOSES IT, which a `.form-modal` never did: the modals this replaces could only be left by
 * clicking their backdrop or their ✕, and this panel has no backdrop to click.
 */
import { useCallback, useEffect, type ReactNode } from 'react';
import './panelWidths.css';
import './FormPanel.css';

/**
 * The Save glyph. `../initiatives/InitiativeDetailPanel.tsx:226-250`'s, verbatim — the same
 * hand-drawn floppy that panel's header Save carries, rather than MUI's `SaveIcon`, because this
 * half of the module does not render MUI (UI_CONVENTIONS §0.1; mixing the two vocabularies in one
 * screen is the mistake nothing in the build catches). The MUI half's panel —
 * `../businessUnits/BusinessUnitDetailPanel.tsx` — uses `SaveIcon` from `@platform/ui-kit` for the
 * same reason in reverse.
 */
function SaveGlyph() {
  return (
    <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true">
      <path
        d="M4 3h9l3 3v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M6.5 3v4.5h6V3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <rect
        x="6.5"
        y="11"
        width="7"
        height="5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}

export interface FormPanelProps {
  open: boolean;
  /** The record's identity, or what is being added. */
  title: string;
  /**
   * SINGLE (the default) or DOUBLE — `panelWidths.css` carries the ruling and the two numbers.
   *
   * Owner, 2026-10-05: single is the starting width for every panel; double exists only for
   * content that genuinely cannot be read narrower. Pass it with a reason at the call site, never
   * because the form would be roomier.
   */
  width?: 'single' | 'double';
  /** The line under it — a code, or what the form is for. */
  subtitle?: string;
  /** Close the panel. Called once the discard guard below has been satisfied. */
  onRequestClose: () => void;
  onSave: () => void;
  /** Disables both buttons and swaps the Save label. */
  busy?: boolean;
  /** The label beside the Save icon. Defaults to "Save". */
  saveLabel?: string;
  /** The label while `busy`. Defaults to "Saving…". */
  busyLabel?: string;
  /**
   * Drop the primary button entirely — for a panel the current user may read but not write.
   * The Assessment Grid's cell panel does this when the initiative is baselined; Cancel is then
   * the only control, exactly as its modal behaved.
   */
  hideSave?: boolean;
  /**
   * What the quiet secondary says. Defaults to "Cancel".
   *
   * A panel WITH a primary says Cancel, because there is something to cancel. A panel with
   * `hideSave` has nothing pending, and "Cancel" there asks the user to abandon a change they were
   * never offered — so the Initiative Setup panel, which is a read-only checklist whose one action
   * lives inside the card with its gate reason beside it, says **Close** instead.
   */
  cancelLabel?: string;
  /**
   * Put the Save glyph on the primary button. Default true, because that is the owner's ruling.
   *
   * The Assessment Grid passes FALSE, and it is the one place where keeping the icon would say
   * something untrue: its primary is **Apply**, which holds the change on the screen, and the
   * write is a separate **Save** in that screen's toolbar — its own hint spells the difference out
   * ("Apply holds the change on this screen. Save in the toolbar writes it"). A floppy on Apply
   * would contradict the sentence under it.
   */
  showSaveIcon?: boolean;
  /**
   * Whether the form holds edits that would be lost.
   *
   * UNSAVED WORK MUST NOT VANISH SILENTLY. When this is true, Cancel, the header's Close and
   * Escape all ask first — `pages/project_management/tracking/PortfolioExceptions.tsx:628-632` is
   * the platform's own guard on closing a right-side panel and this is its clone, the same
   * `window.confirm` in the same shape.
   *
   * It is also a fix rather than a transfer: the `.form-modal` this shell replaces closed on a
   * stray click anywhere on its backdrop and took a half-filled form with it. This panel has no
   * backdrop to click and asks before discarding, so the two ways that work was lost are both gone.
   */
  dirty?: boolean;
  /** The question asked before discarding. Name the record, as the precedent's wording does. */
  discardMessage?: string;
  children: ReactNode;
}

export function FormPanel({
  open,
  title,
  width = 'single',
  subtitle,
  onRequestClose,
  onSave,
  busy = false,
  saveLabel = 'Save',
  busyLabel = 'Saving…',
  hideSave = false,
  cancelLabel = 'Cancel',
  showSaveIcon = true,
  dirty = false,
  discardMessage = 'Discard your unsaved changes?',
  children,
}: FormPanelProps) {
  /* ONE way out, used by all three controls, so none of them can forget to ask. */
  const requestClose = useCallback(() => {
    if (dirty && !window.confirm(discardMessage)) return;
    onRequestClose();
  }, [dirty, discardMessage, onRequestClose]);

  /* Escape leaves the panel — `pages/project_management/tracking/PortfolioExceptions.tsx:634-639`,
     which guards its own right-side drawer the same way. It routes through `requestClose`, so it
     asks before discarding exactly as Cancel does. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, requestClose]);

  if (!open) return null;

  return (
    /* No click-to-close on the overlay: it is transparent and passes its clicks through to the
       screen behind, which is the whole point (`FormPanel.css`). Close and Cancel are the way out,
       and neither can be missed — unlike a `.form-modal` backdrop, which closed on a stray click
       and took the half-filled form with it. */
    <div className="erp-fp-overlay">
      <div
        className={`erp-fp-panel${width === 'double' ? ' erp-fp-panel--double' : ''}`}
        role="dialog"
        aria-modal="false"
        aria-label={title}
      >
        <div className="erp-fp-head">
          <div>
            <h2>{title}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
          <button
            type="button"
            className="erp-fp-close"
            onClick={requestClose}
            title="Close"
            aria-label="Close"
          >
            <svg viewBox="0 0 20 20" width="19" height="19" aria-hidden="true">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.9"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* A real `<form>`, so Enter still submits — every modal this replaces was one, and
            losing that would be a silent behaviour change. */}
        <form
          className="erp-fp-form"
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
        >
          <div className="erp-fp-body">{children}</div>
          <div className="erp-fp-foot">
            <button type="button" className="erp-fp-btn" onClick={requestClose} disabled={busy}>
              {cancelLabel}
            </button>
            {/* The one filled primary on the panel (CLAUDE.md §1.9.3), and the Save icon rides
                with it — owner's words, "Save should be icon Save". */}
            {hideSave ? null : (
              <button type="submit" className="erp-fp-btn erp-fp-btn--primary" disabled={busy}>
                {showSaveIcon ? <SaveGlyph /> : null}
                {busy ? busyLabel : saveLabel}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
